import Enterprise24 from '@carbon/icons-react/es/enterprise/24';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Category } from 'api/timesheets/categories';
import { TimesheetProjectReport } from 'api/timesheets/reports/types';
import { Avatar } from 'components/atoms/avatar';
import {
  NameCell,
  NameCellWithTooltip,
} from 'components/organisms/timesheets/common/cells/name-cell';
import { PercentWithTimeCell } from 'components/organisms/timesheets/common/cells/percent-with-time-cell';
import { BaseCell } from 'components/organisms/timesheets/common/timesheet-table';
import { calculatePercentage } from 'components/organisms/timesheets/common/utils';
import { numberSorter } from 'utils/sorters';
import { FormatTime } from '../../common/format-time';
import { CategoryHeader } from '../common';
import {
  billableMinutesSorter,
  categoryMinutesSorter,
  nonBillableMinutesSorter,
  nonCategoryMinutesSorter,
  projectNameSorter,
  totalMinutesSorter,
} from './sorter';

export const useUserWiseProjectConfig = ({
  noOfRows,
  timesheetCategories,
}: {
  noOfRows: number;
  timesheetCategories: Category[];
}) => {
  const categoryColumns = timesheetCategories
    .sort(
      ({ name: categoryA }: Category, { name: categoryB }: Category): number =>
        categoryA.localeCompare(categoryB),
    )
    .map((category) => {
      return {
        title: (
          <CategoryHeader name={category.name} deleted={category.deleted} />
        ),
        key: `category$${category.id}`,
        width: '200px',
        render: (_, record: TimesheetProjectReport) => {
          const { billableMinutes, nonBillableMinutes, categories } = record;
          const totalMinutesForCategory =
            categories?.find((x) => x.categoryId === category.id)
              ?.totalMinutes ?? 0;

          const percentage = calculatePercentage(
            totalMinutesForCategory,
            billableMinutes,
            nonBillableMinutes,
          );

          return (
            <PercentWithTimeCell
              minutes={totalMinutesForCategory}
              percentage={percentage}
            />
          );
        },
        summaryRender: (_, summaryRecord: TimesheetProjectReport) => {
          const { categories } = summaryRecord;
          const totalMinutesForCategory =
            categories.find((x) => x.categoryId === category.id)
              ?.totalMinutes ?? 0;

          return <FormatTime value={totalMinutesForCategory} />;
        },
        sorter: (a, b) => categoryMinutesSorter(a, b, category.id),
        showSorterTooltip: false,
      };
    });

  return [
    {
      title: (
        <FormattedMessage
          id="timesheets.column.projects"
          values={{ count: noOfRows }}
        />
      ),
      key: 'projects',
      width: '310px',
      fixed: 'left',
      render: (_, record: TimesheetProjectReport) => {
        const { project } = record;

        if (project) {
          return (
            <NameCellWithTooltip
              logo={
                <Avatar
                  name={project.projectName}
                  src={project.customer.companyLogoUrl}
                />
              }
              primaryText={project.projectName}
              secondaryText={project.customer.companyName}
            />
          );
        }

        return (
          <NameCell
            logo={<Enterprise24 />}
            primaryText={
              <FormattedMessage id="timesheets.labels.non_project" />
            }
          />
        );
      },
      summaryRender: () => <FormattedMessage id="timesheets.column.total" />,
      sorter: projectNameSorter,
      defaultSortOrder: 'ascend',
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="reports.columns.allocated.hours" />,
      key: 'allocatedHours',
      width: '200px',
      render: (_, record: TimesheetProjectReport) => {
        const { allocatedMinutes } = record;

        return (
          <BaseCell>
            <FormatTime value={allocatedMinutes} />
          </BaseCell>
        );
      },
      summaryRender: (_, summaryRecord: TimesheetProjectReport) => {
        return <FormatTime value={summaryRecord.allocatedMinutes} />;
      },
      sorter: (
        { allocatedMinutes: a }: TimesheetProjectReport,
        { allocatedMinutes: b }: TimesheetProjectReport,
      ) => numberSorter(a, b),
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.hours_tracked" />,
      key: 'hoursTracked',
      width: '200px',
      render: (_, record: TimesheetProjectReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        return (
          <BaseCell>
            <FormatTime value={billableMinutes + nonBillableMinutes} />
          </BaseCell>
        );
      },
      summaryRender: (_, summaryRecord: TimesheetProjectReport) => (
        <FormatTime
          value={
            summaryRecord.billableMinutes + summaryRecord.nonBillableMinutes
          }
        />
      ),
      sorter: totalMinutesSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.billable_hours" />,
      key: 'billableHours',
      width: '200px',
      render: (_, record: TimesheetProjectReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        const percentage = calculatePercentage(
          billableMinutes,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={billableMinutes}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetProjectReport) => (
        <FormatTime value={summaryRecord.billableMinutes} />
      ),
      sorter: billableMinutesSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.non_billable_hours" />,
      key: 'nonBillableHours',
      width: '200px',
      render: (_, record: TimesheetProjectReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        const percentage = calculatePercentage(
          nonBillableMinutes,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={nonBillableMinutes}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetProjectReport) => (
        <FormatTime value={summaryRecord.nonBillableMinutes} />
      ),
      sorter: nonBillableMinutesSorter,
      showSorterTooltip: false,
    },
    ...categoryColumns,
    {
      title: <FormattedMessage id="timesheets.column.no_category" />,
      key: 'noCategory',
      width: '200px',
      render: (_, record: TimesheetProjectReport) => {
        const { billableMinutes, nonBillableMinutes, categories } = record;
        const totalMinutesForCategory =
          categories?.find((x) => !x.categoryId)?.totalMinutes ?? 0;

        const percentage = calculatePercentage(
          totalMinutesForCategory,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={totalMinutesForCategory}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetProjectReport) => {
        const { categories } = summaryRecord;
        const totalMinutesForCategory =
          categories?.find((x) => !x.categoryId)?.totalMinutes ?? 0;

        return <FormatTime value={totalMinutesForCategory} />;
      },
      sorter: nonCategoryMinutesSorter,
      showSorterTooltip: false,
    },
  ];
};
